import { CookiesKeys } from '@/constants/enums/CookiesEnum';
import dayjs from 'dayjs';
import Cookies from 'universal-cookie';

type TokenPayload = {
  member_id: string;
  FirstName: string;
  LastName: string;
  Email: string;
  userId: string;
  fdtk: string;
  nickName: string;
  sex: string;
  planType: string;
  nbf: number;
  exp: number;
  iss: string;
  iat?: number;
  dtk?: string;
  /** 網站版本 (V1 或 V2) */
  siteVersion: string;
};

// Cookie 設定
const cookieOptions = {
  path: '/',
  domain: '.funday.asia',
  sameSite: 'lax' as const,
  secure: import.meta.env.VITE_IS_PROD === 'true',
  expires: dayjs().add(7, 'day').toDate()
};

const cookie = new Cookies();

/**
 * 是否為空字串
 * @param str - 字串
 * @returns `{boolean}` Whether the string is empty.
 */
function isEmptyString(str: string): boolean {
  const regExp: RegExp = /^\s*$/;
  return regExp.exec(str) !== null;
}

/**
 * 解析 JWT
 * @param token - JWT 權杖
 * @returns `{TokenPayload}` 解析值
 */
export function parseJwtToken(token: string): TokenPayload {
  try {
    if (isEmptyString(token)) return {} as TokenPayload;

    const segments = token.split('.')[1] || '';
    const decoded: string =
      typeof window !== 'undefined'
        ? window.atob(segments)
        : Buffer.from(segments, 'base64').toString('utf-8');

    return JSON.parse(decoded);
  } catch (errors: unknown) {
    throw new Error(`Fail on parse JWT token. ${JSON.stringify(errors)}`);
  }
}

/**
 * 權杖是否過期
 * @param token - JWT 權杖
 * @remarks 後端 Access 權杖時效為一個月，前端縮短為七天過期 (資安考量)
 */
export function tokenExpire(token: string): boolean {
  const payload = parseJwtToken(token);
  if (!payload.exp || typeof payload.exp !== 'number') return true;

  const sevenDaysMs: number = 60000 * 60 * 24 * 7; // 7 天（毫秒）
  const issuedExp: number = payload.exp * 1000; // JWT 過期時間（毫秒）

  return Date.now() >= issuedExp - sevenDaysMs;
}

/**
 * 取得 JWT 權杖
 * @returns `{string}` JWT 權杖
 */
export function getToken(): string {
  return cookie.get(CookiesKeys.AuthTokenName) || '';
}

/**
 * 取得 JWT 權杖的 payload
 * @returns `{TokenPayload}` 解析值
 */
export function getTokenPayload(): TokenPayload {
  return parseJwtToken(getToken());
}

/**
 * 設置 JWT 權杖
 * @param token - JWT 權杖
 * @remarks 設置 cookie 時效為七天
 */
export function setToken(token: string): void {
  cookie.set(CookiesKeys.AuthTokenName, token, cookieOptions);
}

/**
 * 移除 JWT 權杖
 */
export function removeToken(): void {
  cookie.remove(CookiesKeys.AuthTokenName, {
    path: '/',
    domain: '.funday.asia'
  });
}

/**
 *  取得登入狀態
 * @returns `{boolean}` 登入狀態
 */
export function loginState(): boolean {
  return cookie.get(CookiesKeys.IS_LOGGED_IN) || false;
}

/**
 * 設置登入狀態
 * @param isLoggedIn - 登入狀態
 */
export function setLoginStateCookie(isLoggedIn: boolean): void {
  cookie.set(CookiesKeys.IS_LOGGED_IN, isLoggedIn, cookieOptions);
}
